/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import React, {MouseEventHandler, ReactNode} from 'react';
import {Button} from "./button";
import {OutlinedButton} from "./outlinedButton";
import {ControlContext, ThemeShape} from "../util/controlContext";
import {ControlPadding, ControlVariant} from "../util/interfaces";
import SimpleButton from "./simpleButton";
import {generateComponentId} from '../util/componentUtil';

interface Props extends Readonly<{ children?: ReactNode }> {
    onClick: MouseEventHandler;
    label: string;
    containerClass: string;
    className: string;
    labelClass: string;
    id?: string;
    padding: 'small' | 'normal' | 'large';
    type: ControlVariant | string;
    disabled: boolean;
    withRipple: boolean;
    baseColor?: string;
    textColor?: string;
    contextStyle?: ThemeShape;
    title?: string;
    primary: boolean;
    tabIndex?: number;
}

interface State {
    isHovered: boolean;
}

export default class ButtonController extends React.Component<Props, State> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    static defaultProps = {
        containerClass: '',
        className: '',
        labelClass: '',
        padding: 'normal',
        disabled: false,
        withRipple: true,
        type: ControlVariant.STANDARD,
        primary: true,
    };

    state = {
        isHovered: false,
    };

    private getProps = (): Props => {
        const origProps = this.props;
        return {
            ...origProps,
            type: this.context.controlVariant || this.props.type,
        };
    };

    private id: string = generateComponentId(this.props.id);

    private getContainer = (): HTMLElement | null => {
        return document.getElementById(this.id);
    };

    private getRipple = (): HTMLElement | null => {
        const container = this.getContainer();
        if (container === null) return null;
        return container.querySelector('div.ripple');
    };

    onClick = (evt: any) => {
        if (!this.props.disabled)
            this.props.onClick(evt);
    };

    onMouseEnter = () => {
        this.setState({isHovered: true});
    };

    onMouseLeave = () => {
        this.setState({isHovered: false});
    };

    onMouseDown = (evt: any) => {
        if (this.props.withRipple && !this.props.disabled)
            this.addRipple(evt);
    };

    addRipple = (evt: any) => {
        const container = this.getContainer();
        if (container !== null) {
            const ripple = this.getRipple();
            if (ripple !== null) {
                if (container.classList.contains('rippling'))
                    container.classList.remove('rippling');
                const rect = evt.target.getBoundingClientRect();
                ripple.style.setProperty('left', evt.clientX - rect.left + 'px');
                ripple.style.setProperty('top', evt.clientY - rect.top + 'px');
                ripple.addEventListener('transitionend', this.removeRipple);
                container.classList.add('rippling');
            }
        }
    };

    removeRipple = () => {
        const ripple = this.getRipple();
        const container = this.getContainer();
        if (ripple !== null && container !== null) {
            ripple.removeEventListener('transitionend', this.removeRipple);
            container.classList.remove('rippling');
        }
    };

    getBaseColor = (): string => {
        const {baseColor} = this.props;
        if (baseColor !== undefined && baseColor !== null) return baseColor;
        return this.getContextStyle().baseColor;
    };

    getTextColor = (): string => {
        const {textColor} = this.props;
        if (textColor !== null && textColor !== undefined) return textColor;
        return this.getContextStyle().contrastColor;
    };

    getContextStyle = (): ThemeShape => {
        const {contextStyle} = this.props;
        if (contextStyle !== null && contextStyle !== undefined) return contextStyle;
        // noinspection JSDeprecatedSymbols
        return this.context;
    };

    componentWillUnmount = () => {
        this.removeRipple();
    };

    render = () => {
        const {
                labelClass, containerClass, disabled, padding, withRipple, label, type, title, baseColor, textColor,
                primary, children, tabIndex, className
            } = this.getProps(),
            {isHovered} = this.state, controlType = type === 'standard' ? ControlVariant.STANDARD :
            type === 'outlined' ? ControlVariant.OUTLINED : ControlVariant.SIMPLE;
        const controlPadding = padding === 'small' ? ControlPadding.SMALL :
            padding === 'normal' ? ControlPadding.NORMAL : ControlPadding.LARGE;
        if (controlType === ControlVariant.STANDARD)
            return (
                <Button labelClass={labelClass} containerClass={containerClass + className} disabled={disabled}
                        id={this.id} onClick={this.onClick} padding={padding} withRipple={withRipple}
                        baseColor={this.getBaseColor()} textColor={this.getTextColor()} label={label}
                        onMouseEnter={this.onMouseEnter} contextStyle={this.getContextStyle()} title={title}
                        isHovered={isHovered} onMouseLeave={this.onMouseLeave} onMouseDown={this.onMouseDown}
                        tabIndex={tabIndex}>
                    {children}
                </Button>
            );
        else if (controlType === ControlVariant.OUTLINED) return (
            <OutlinedButton onClick={this.onClick} onMouseEnter={this.onMouseEnter}
                            onMouseLeave={this.onMouseLeave} onMouseDown={this.onMouseDown} label={label}
                            containerClass={containerClass + className} labelClass={labelClass} id={this.id}
                            title={title} padding={padding} disabled={disabled} withRipple={withRipple}
                            isHovered={isHovered} baseColor={this.getBaseColor()} contextStyle={this.getContextStyle()}
                            tabIndex={tabIndex}>
                {children}
            </OutlinedButton>
        );
        else return (
                <SimpleButton onClick={this.onClick} onMouseEnter={this.onMouseEnter} onMouseLeave={this.onMouseLeave}
                              onMouseDown={this.onMouseDown} label={label} containerClass={containerClass + className}
                              labelClass={labelClass} id={this.id} padding={controlPadding} disabled={disabled}
                              isHovered={isHovered} baseColor={baseColor} textColor={textColor} withRipple={withRipple}
                              primary={primary} title={title} tabIndex={tabIndex}>{children}</SimpleButton>
            );
    };
}